// Auto-heal routine — probe server registration, re-provision if stale.
// Pure function, no React/Zustand — unit-testable in plain Node.

import * as keycast from '@/lib/keycastClient'
import { getServerAuthorizationLabel } from '@/lib/baseUrl'
import { ApiError } from '@/lib/apiError'
import {
  browserTimezone,
  fetchServerRestaurantStatus,
  provisionServer,
  usableTimezone,
  type RestaurantSigner,
  type ServerRestaurantSnapshot,
} from '@/services/serverRegistration'
import { ensureMerchantSession } from '@/services/merchantAuth'

export interface AutoHealDeps {
  teamId: number
  restaurantPubkey: string
  policyId: number
  relays: string[]
  signAsRestaurant: RestaurantSigner
  ensureMerchantSessionFn?: (
    pubkey: string,
    signer: RestaurantSigner,
  ) => Promise<void>
  probeStatus?: (
    pubkey: string,
  ) => Promise<ServerRestaurantSnapshot | 'not_found'>
  createAuthorization?: typeof keycast.createAuthorization
  provisionServerFn?: typeof provisionServer
  timezoneFn?: () => string
}

export type AutoHealOutcome = 'ready' | 'healed' | 'failed'

/**
 * Probe the server for current provisioning status, and if anything
 * other than `ready` comes back, mint a fresh Keycast authorization
 * and re-register. Swallows all errors — returns 'failed' on any
 * exception so the caller can show a non-blocking banner.
 */
export async function runServerProvisioningAutoHeal(
  deps: AutoHealDeps,
): Promise<AutoHealOutcome> {
  const authenticate = deps.ensureMerchantSessionFn ?? ensureMerchantSession
  const probe = deps.probeStatus ?? fetchServerRestaurantStatus
  const createAuth = deps.createAuthorization ?? keycast.createAuthorization
  const provision = deps.provisionServerFn ?? provisionServer
  const tz = deps.timezoneFn ?? browserTimezone
  const label = getServerAuthorizationLabel()

  try {
    await authenticate(deps.restaurantPubkey, deps.signAsRestaurant)

    const snapshot = await probe(deps.restaurantPubkey)
    if (snapshot !== 'not_found') {
      if (snapshot.server_authorization_label === label) {
        return snapshot.server_provisioning_status === 'ready'
          ? 'ready'
          : 'failed'
      }
    }

    const serverAuth = await createAuth(
      deps.teamId,
      deps.restaurantPubkey,
      deps.policyId,
      deps.relays,
      { label },
    )
    await provision({
      restaurantPubkey: deps.restaurantPubkey,
      serverBunkerUrl: serverAuth.bunker_url,
      authorizationLabel: label,
      // Registration writes the zone it is sent, so send back the registered
      // one. The browser's is only for a restaurant with none (account#248),
      // or with one this browser does not recognise.
      timezone: (snapshot !== 'not_found' && usableTimezone(snapshot.timezone)) || tz(),
      signAsRestaurant: deps.signAsRestaurant,
    })

    return 'healed'
  } catch (err) {
    if (err instanceof ApiError) {
      console.warn(
        `[auto-heal] server provisioning failed (${err.code}): ${err.message}`,
      )
    } else {
      console.warn('[auto-heal] server provisioning failed:', err)
    }
    return 'failed'
  }
}
